import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { getUser } from '../auth';

function Settings() {
  const navigate = useNavigate();
  const currentUser = getUser();

  const [settings, setSettings] = useState({
    company_name: 'نظام الإشارة الإداري',
    company_phone: '',
    company_address: '',
    default_currency: 'LYD',
    currency_name: 'دينار ليبي',
    allow_negative_wallets: true,
    backup_note: ''
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [activeSection, setActiveSection] = useState('company');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [backupResult, setBackupResult] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [securityLoading, setSecurityLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError('');
      setMessage('');

      const response = await apiFetch('/settings');

      if (!response) {
        setError('لم يتم استلام رد من السيرفر. سجل دخول من جديد أو تأكد أن السيرفر يعمل.');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'حدث خطأ أثناء جلب الإعدادات');
      }

      if (data.settings) {
        setSettings({
          company_name: data.settings.company_name || 'نظام الإشارة الإداري',
          company_phone: data.settings.company_phone || '',
          company_address: data.settings.company_address || '',
          default_currency: data.settings.default_currency || 'LYD',
          currency_name: data.settings.currency_name || 'دينار ليبي',
          allow_negative_wallets: data.settings.allow_negative_wallets !== false,
          backup_note: data.settings.backup_note || ''
        });
      }
    } catch (err) {
      setError(err.message || 'حدث خطأ أثناء جلب الإعدادات');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setSettings((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;

    setPasswordForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const resetPasswordForm = () => {
    setPasswordForm({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError('');
      setMessage('');

      if (!settings.company_name.trim()) {
        throw new Error('اسم الشركة / النظام مطلوب');
      }

      const payload = {
        company_name: settings.company_name.trim(),
        company_phone: settings.company_phone.trim(),
        company_address: settings.company_address.trim(),
        default_currency: settings.default_currency.trim() || 'LYD',
        currency_name: settings.currency_name.trim() || 'دينار ليبي',
        allow_negative_wallets: settings.allow_negative_wallets,
        backup_note: settings.backup_note.trim()
      };

      const response = await apiFetch('/settings', {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      if (!response) {
        setError('لم يتم استلام رد من السيرفر. سجل دخول من جديد أو تأكد أن السيرفر يعمل.');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'حدث خطأ أثناء حفظ الإعدادات');
      }

      setMessage('تم حفظ إعدادات النظام بنجاح');

      if (data.settings) {
        setSettings({
          company_name: data.settings.company_name || 'نظام الإشارة الإداري',
          company_phone: data.settings.company_phone || '',
          company_address: data.settings.company_address || '',
          default_currency: data.settings.default_currency || 'LYD',
          currency_name: data.settings.currency_name || 'دينار ليبي',
          allow_negative_wallets: data.settings.allow_negative_wallets !== false,
          backup_note: data.settings.backup_note || ''
        });
      }
    } catch (err) {
      setError(err.message || 'حدث خطأ أثناء حفظ الإعدادات');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    try {
      setPasswordSaving(true);
      setError('');
      setMessage('');

      if (!passwordForm.current_password) {
        throw new Error('كلمة المرور الحالية مطلوبة');
      }

      if (!passwordForm.new_password || passwordForm.new_password.length < 4) {
        throw new Error('كلمة المرور الجديدة يجب أن تكون 4 أرقام أو أحرف على الأقل');
      }

      if (passwordForm.new_password !== passwordForm.confirm_password) {
        throw new Error('تأكيد كلمة المرور غير مطابق');
      }

      const response = await apiFetch('/change-password', {
        method: 'PUT',
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });

      if (!response) {
        setError('لم يتم استلام رد من السيرفر. قد نحتاج تفعيل API تغيير كلمة المرور في السيرفر.');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
          data.error ||
          'فشل تغيير كلمة المرور. تأكد من كلمة المرور الحالية وحاول مرة أخرى.'
        );
      }

      setMessage('تم تغيير كلمة المرور بنجاح');
      resetPasswordForm();
    } catch (err) {
      setError(err.message || 'حدث خطأ أثناء تغيير كلمة المرور');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleBackup = async () => {
    const confirmed = window.confirm('هل تريد إنشاء نسخة احتياطية الآن؟');
    if (!confirmed) return;

    try {
      setBackupLoading(true);
      setError('');
      setMessage('');
      setBackupResult(null);

      const response = await apiFetch('/backup', {
        method: 'POST'
      });

      if (!response) {
        setError('لم يتم استلام رد من السيرفر. سجل دخول من جديد أو تأكد أن السيرفر يعمل.');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'حدث خطأ أثناء إنشاء النسخة الاحتياطية');
      }

      setBackupResult(data);
      setMessage(`تم إنشاء النسخة الاحتياطية بنجاح${data.file_name ? `: ${data.file_name}` : ''}`);
    } catch (err) {
      setError(err.message || 'حدث خطأ أثناء إنشاء النسخة الاحتياطية');
    } finally {
      setBackupLoading(false);
    }
  };



  const fetchSystemHealth = async () => {
    try {
      setHealthLoading(true);
      setError('');
      setMessage('');
      const response = await apiFetch('/system-health');
      if (!response) {
        setError('لم يتم استلام رد من السيرفر');
        return;
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.error || 'فشل فحص صحة النظام');
      setHealth(data);
      setMessage('تم تحديث لوحة صحة النظام');
    } catch (err) {
      setError(err.message || 'حدث خطأ أثناء فحص صحة النظام');
    } finally {
      setHealthLoading(false);
    }
  };

  const logoutMyDevices = async () => {
    const confirmed = window.confirm('سيتم تسجيل خروجك من كل الأجهزة وستحتاج للدخول من جديد. هل تريد المتابعة؟');
    if (!confirmed) return;
    try {
      setSecurityLoading(true);
      const response = await apiFetch('/auth/logout-my-devices', { method: 'POST' });
      const data = response ? await response.json().catch(() => ({})) : {};
      if (!response?.ok) throw new Error(data.message || 'فشل تسجيل الخروج من الأجهزة');
      localStorage.removeItem('eshara_token');
      localStorage.removeItem('eshara_user');
      window.location.href = '/login';
    } catch (err) {
      setError(err.message || 'حدث خطأ أثناء تسجيل الخروج من الأجهزة');
    } finally {
      setSecurityLoading(false);
    }
  };

  const logoutAllUsers = async () => {
    const confirmed = window.confirm('تحذير: سيتم إنهاء جلسات كل المستخدمين. هل أنت متأكد؟');
    if (!confirmed) return;
    try {
      setSecurityLoading(true);
      setError('');
      setMessage('');
      const response = await apiFetch('/auth/logout-all-users', { method: 'POST' });
      const data = response ? await response.json().catch(() => ({})) : {};
      if (!response?.ok) throw new Error(data.message || 'فشل تسجيل خروج كل المستخدمين');
      setMessage(data.message || 'تم إنهاء كل الجلسات بنجاح');
    } catch (err) {
      setError(err.message || 'حدث خطأ أثناء تسجيل خروج الجميع');
    } finally {
      setSecurityLoading(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '-';

    try {
      return new Date(value).toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const sections = [
    { id: 'company', label: 'بيانات الشركة', icon: '🏢' },
    { id: 'wallets', label: 'قواعد المحافظ', icon: '💰' },
    { id: 'password', label: 'كلمة المرور', icon: '🔐' },
    { id: 'backup', label: 'النسخ الاحتياطي', icon: '💾' },
    { id: 'security', label: 'الأمان والصحة', icon: '🛡️' }
  ];

  const previewCards = useMemo(() => {
    return [
      { label: 'اسم النظام', value: settings.company_name || 'نظام الإشارة الإداري', icon: '🏷️' },
      { label: 'العملة', value: `${settings.default_currency || 'LYD'} — ${settings.currency_name || 'دينار ليبي'}`, icon: '💱' },
      { label: 'الهاتف', value: settings.company_phone || 'غير محدد', icon: '📞' },
      { label: 'المحافظ بالسالب', value: settings.allow_negative_wallets ? 'مسموح' : 'غير مسموح', icon: '📉' }
    ];
  }, [settings]);

  return (
    <div dir="rtl" className="p-4 md:p-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-br from-gray-950 via-orange-900 to-black text-white rounded-3xl p-5 md:p-8 shadow-lg mb-6">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-xl mb-5"
          >
            ← رجوع للرئيسية
          </button>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold">
                ⚙️ إعدادات النظام
              </h1>
              <p className="text-orange-100 mt-3 text-sm md:text-base">
                بيانات الشركة، العملة، قواعد المحافظ، كلمة المرور، والنسخ الاحتياطي
              </p>
            </div>

            <div className="bg-white/10 border border-white/10 rounded-3xl p-5 min-w-full lg:min-w-[300px]">
              <p className="text-orange-100 text-sm">المستخدم الحالي</p>
              <p className="text-white font-bold text-2xl mt-2">
                {currentUser?.name || '-'}
              </p>
              <p className="text-orange-200 text-xs mt-2">
                {currentUser?.role === 'owner' ? 'مالك النظام' : 'مدير'}
              </p>
            </div>
          </div>
        </div>

        {loading && (
          <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 rounded-2xl p-4">
            جاري تحميل إعدادات النظام...
          </div>
        )}

        {message && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 rounded-2xl p-4">
            ✅ {message}
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4">
            ⚠️ {error}
          </div>
        )}

        {/* Preview */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          {previewCards.map((card) => (
            <div key={card.label} className="bg-white rounded-3xl shadow p-5 border border-gray-100">
              <p className="text-3xl mb-3">{card.icon}</p>
              <p className="text-gray-500 text-sm">{card.label}</p>
              <p className="text-gray-900 font-bold mt-2 break-words">{card.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-3xl shadow p-3 mb-6">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {sections.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => {
                  setActiveSection(section.id);
                  setError('');
                  setMessage('');
                }}
                className={`py-3 px-4 rounded-2xl font-bold transition ${
                  activeSection === section.id
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
                }`}
              >
                {section.icon} {section.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Company */}
          {activeSection === 'company' && (
            <Section title="🏢 بيانات الشركة / النظام" subtitle="هذه البيانات تظهر في التقارير والفواتير لاحقاً">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>اسم الشركة / النظام</Label>
                  <Input
                    name="company_name"
                    value={settings.company_name}
                    onChange={handleChange}
                    placeholder="نظام الإشارة الإداري"
                  />
                </div>

                <div>
                  <Label>رقم الهاتف</Label>
                  <Input
                    name="company_phone"
                    value={settings.company_phone}
                    onChange={handleChange}
                    placeholder="0910000000"
                  />
                </div>

                <div>
                  <Label>العملة الافتراضية</Label>
                  <Input
                    name="default_currency"
                    value={settings.default_currency}
                    onChange={handleChange}
                    placeholder="LYD"
                  />
                </div>

                <div>
                  <Label>اسم العملة</Label>
                  <Input
                    name="currency_name"
                    value={settings.currency_name}
                    onChange={handleChange}
                    placeholder="دينار ليبي"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>العنوان</Label>
                  <Textarea
                    name="company_address"
                    value={settings.company_address}
                    onChange={handleChange}
                    rows="4"
                    placeholder="عنوان الشركة"
                  />
                </div>
              </div>
            </Section>
          )}

          {/* Wallet Rules */}
          {activeSection === 'wallets' && (
            <Section title="💰 قواعد المحافظ" subtitle="قواعد المحاسبة الخاصة بالمحافظ الرسمية">
              <label className="flex items-start gap-4 bg-gray-50 border border-gray-100 rounded-3xl p-5 cursor-pointer">
                <input
                  type="checkbox"
                  name="allow_negative_wallets"
                  checked={settings.allow_negative_wallets}
                  onChange={handleChange}
                  className="w-6 h-6 mt-1"
                />

                <span>
                  <span className="block text-gray-900 font-bold text-lg">
                    السماح للمحافظ أن تكون بالسالب
                  </span>
                  <span className="block text-gray-600 text-sm mt-2 leading-7">
                    هذه القاعدة مفعّلة لأن المحافظ كلها تابعة لشركة واحدة ويتم تجميع أرصدتها محاسبياً.
                    السالب يعني أن هناك شحن أو التزام يحتاج تسوية لاحقاً.
                  </span>
                </span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                <RuleCard
                  title="المصروف"
                  text="المصروف يخصم من رصيد المستخدم، وليس من المحافظ."
                />
                <RuleCard
                  title="الشحن"
                  text="الشحن ينقص المحفظة المختارة ويزيد رصيد/مديونية المحل."
                />
                <RuleCard
                  title="القبض"
                  text="القبض من المحل يزيد رصيد المستخدم الذي قبض المال."
                />
                <RuleCard
                  title="مصرفي"
                  text="مصرفي يحتاج لاحقاً وصل وتأكيد الشركة وحالة الشحن."
                />
              </div>
            </Section>
          )}

          {(activeSection === 'company' || activeSection === 'wallets') && (
            <div className="flex flex-col md:flex-row gap-3 mb-6">
              <button
                type="submit"
                disabled={saving}
                className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white px-6 py-4 rounded-2xl font-bold"
              >
                {saving ? 'جاري الحفظ...' : '✅ حفظ الإعدادات'}
              </button>

              <button
                type="button"
                onClick={fetchSettings}
                disabled={loading}
                className="bg-gray-800 hover:bg-black disabled:opacity-50 text-white px-6 py-4 rounded-2xl font-bold"
              >
                🔄 تحديث البيانات
              </button>
            </div>
          )}


          {activeSection === 'security' && (
            <Section title="🛡️ الأمان وصحة النظام" subtitle="إنهاء الجلسات ومراقبة حالة السيرفر وقاعدة البيانات">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 border border-gray-100 rounded-3xl p-5">
                  <h3 className="font-bold text-xl text-gray-900 mb-2">🔐 الجلسات</h3>
                  <p className="text-sm text-gray-600 leading-7 mb-4">استخدم هذه الأزرار عند فقدان هاتف أو ترك جلسة مفتوحة.</p>
                  <div className="flex flex-col gap-3">
                    <button type="button" onClick={logoutMyDevices} disabled={securityLoading} className="bg-gray-900 hover:bg-black disabled:bg-gray-300 text-white font-bold py-3 px-5 rounded-xl">
                      تسجيل خروج من كل أجهزتي
                    </button>
                    {currentUser?.role === 'owner' && (
                      <button type="button" onClick={logoutAllUsers} disabled={securityLoading} className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-bold py-3 px-5 rounded-xl">
                        تسجيل خروج كل المستخدمين
                      </button>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-100 rounded-3xl p-5">
                  <h3 className="font-bold text-xl text-gray-900 mb-2">🩺 صحة النظام</h3>
                  <p className="text-sm text-gray-600 leading-7 mb-4">يعرض حالة السيرفر وقاعدة البيانات وآخر نسخة احتياطية.</p>
                  <button type="button" onClick={fetchSystemHealth} disabled={healthLoading} className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white font-bold py-3 px-5 rounded-xl w-full">
                    {healthLoading ? 'جاري الفحص...' : 'فحص صحة النظام'}
                  </button>
                </div>
              </div>
              {health && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <HealthCard label="السيرفر" value={health.server?.status || '-'} hint={`الذاكرة: ${health.server?.memory_mb || 0} MB`} />
                  <HealthCard label="قاعدة البيانات" value={health.database?.status || '-'} hint={formatDateTime(health.database?.time)} />
                  <HealthCard label="المستخدمون" value={health.counts?.users ?? 0} hint="إجمالي المستخدمين" />
                  <HealthCard label="العمليات" value={health.counts?.transactions ?? 0} hint="إجمالي العمليات" />
                  <HealthCard label="المحلات" value={health.counts?.stores ?? 0} hint="محلات غير محذوفة" />
                  <HealthCard label="إشعارات غير مقروءة" value={health.counts?.unread_notifications ?? 0} hint="تحتاج مراجعة" />
                  <HealthCard label="مدة تشغيل السيرفر" value={`${Math.floor((health.server?.uptime_seconds || 0) / 60)} دقيقة`} hint="من آخر تشغيل" />
                  <HealthCard label="آخر نسخة احتياطية" value={health.backup?.file_name || 'لا توجد'} hint={formatDateTime(health.backup?.created_at)} />
                </div>
              )}
            </Section>
          )}

        </form>

        {/* Password */}
        {activeSection === 'password' && (
          <Section title="🔐 تغيير كلمة المرور" subtitle="تغيير كلمة مرور المستخدم الحالي">
            <form onSubmit={handlePasswordSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>كلمة المرور الحالية</Label>
                <Input
                  type="password"
                  name="current_password"
                  value={passwordForm.current_password}
                  onChange={handlePasswordChange}
                  placeholder="كلمة المرور الحالية"
                />
              </div>

              <div>
                <Label>كلمة المرور الجديدة</Label>
                <Input
                  type="password"
                  name="new_password"
                  value={passwordForm.new_password}
                  onChange={handlePasswordChange}
                  placeholder="4 أرقام أو أحرف فأكثر"
                />
              </div>

              <div>
                <Label>تأكيد كلمة المرور</Label>
                <Input
                  type="password"
                  name="confirm_password"
                  value={passwordForm.confirm_password}
                  onChange={handlePasswordChange}
                  placeholder="أعد كتابة كلمة المرور"
                />
              </div>

              <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="submit"
                  disabled={passwordSaving}
                  className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white px-6 py-4 rounded-2xl font-bold"
                >
                  {passwordSaving ? 'جاري التغيير...' : '✅ تغيير كلمة المرور'}
                </button>

                <button
                  type="button"
                  onClick={resetPasswordForm}
                  disabled={passwordSaving}
                  className="bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-800 px-6 py-4 rounded-2xl font-bold"
                >
                  مسح الحقول
                </button>
              </div>
    

          {activeSection === 'security' && (
            <Section title="🛡️ الأمان وصحة النظام" subtitle="إنهاء الجلسات ومراقبة حالة السيرفر وقاعدة البيانات">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 border border-gray-100 rounded-3xl p-5">
                  <h3 className="font-bold text-xl text-gray-900 mb-2">🔐 الجلسات</h3>
                  <p className="text-sm text-gray-600 leading-7 mb-4">استخدم هذه الأزرار عند فقدان هاتف أو ترك جلسة مفتوحة.</p>
                  <div className="flex flex-col gap-3">
                    <button type="button" onClick={logoutMyDevices} disabled={securityLoading} className="bg-gray-900 hover:bg-black disabled:bg-gray-300 text-white font-bold py-3 px-5 rounded-xl">
                      تسجيل خروج من كل أجهزتي
                    </button>
                    {currentUser?.role === 'owner' && (
                      <button type="button" onClick={logoutAllUsers} disabled={securityLoading} className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-bold py-3 px-5 rounded-xl">
                        تسجيل خروج كل المستخدمين
                      </button>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-100 rounded-3xl p-5">
                  <h3 className="font-bold text-xl text-gray-900 mb-2">🩺 صحة النظام</h3>
                  <p className="text-sm text-gray-600 leading-7 mb-4">يعرض حالة السيرفر وقاعدة البيانات وآخر نسخة احتياطية.</p>
                  <button type="button" onClick={fetchSystemHealth} disabled={healthLoading} className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white font-bold py-3 px-5 rounded-xl w-full">
                    {healthLoading ? 'جاري الفحص...' : 'فحص صحة النظام'}
                  </button>
                </div>
              </div>
              {health && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <HealthCard label="السيرفر" value={health.server?.status || '-'} hint={`الذاكرة: ${health.server?.memory_mb || 0} MB`} />
                  <HealthCard label="قاعدة البيانات" value={health.database?.status || '-'} hint={formatDateTime(health.database?.time)} />
                  <HealthCard label="المستخدمون" value={health.counts?.users ?? 0} hint="إجمالي المستخدمين" />
                  <HealthCard label="العمليات" value={health.counts?.transactions ?? 0} hint="إجمالي العمليات" />
                  <HealthCard label="المحلات" value={health.counts?.stores ?? 0} hint="محلات غير محذوفة" />
                  <HealthCard label="إشعارات غير مقروءة" value={health.counts?.unread_notifications ?? 0} hint="تحتاج مراجعة" />
                  <HealthCard label="مدة تشغيل السيرفر" value={`${Math.floor((health.server?.uptime_seconds || 0) / 60)} دقيقة`} hint="من آخر تشغيل" />
                  <HealthCard label="آخر نسخة احتياطية" value={health.backup?.file_name || 'لا توجد'} hint={formatDateTime(health.backup?.created_at)} />
                </div>
              )}
            </Section>
          )}

        </form>

            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mt-5 text-sm text-blue-800 leading-7">
              هذا القسم مفعّل الآن. أدخل كلمة المرور الحالية ثم كلمة مرور جديدة من 4 أرقام أو أحرف فأكثر.
            </div>
          </Section>
        )}

        {/* Backup */}
        {activeSection === 'backup' && (
          <Section title="💾 النسخ الاحتياطي" subtitle="إنشاء نسخة احتياطية من قاعدة البيانات / البيانات المهمة">
            <div className="mb-4">
              <Label>ملاحظات النسخ الاحتياطي</Label>
              <Textarea
                name="backup_note"
                value={settings.backup_note}
                onChange={handleChange}
                rows="5"
                placeholder="مثال: نسخة قبل التعديل على النظام..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
              <button
                type="button"
                onClick={handleBackup}
                disabled={backupLoading}
                className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white px-6 py-4 rounded-2xl font-bold"
              >
                {backupLoading ? 'جاري إنشاء النسخة...' : '💾 إنشاء نسخة احتياطية'}
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white px-6 py-4 rounded-2xl font-bold"
              >
                حفظ ملاحظة النسخ الاحتياطي
              </button>

              <button
                type="button"
                onClick={() => navigate('/archive')}
                className="bg-gray-900 hover:bg-black text-white px-6 py-4 rounded-2xl font-bold"
              >
                🗂️ فتح مركز الأرشفة
              </button>
            </div>

            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 text-sm text-gray-700 leading-7">
              يتم حفظ النسخة داخل مجلد <b>backups</b> في مجلد المشروع.
              يمكنك الآن فتح <b>مركز الأرشفة</b> لعرض ملفات النسخ، تحميلها، تنظيف النسخ القديمة، وأرشفة وصولات مصرفي لتوفير المساحة.
              الاسترجاع الكامل للنسخ الاحتياطية يبقى إجراءً يدوياً عند الحاجة حتى نضيف زر استرجاع آمن لاحقاً.
            </div>

            {backupResult && (
              <div className="bg-green-50 border border-green-100 rounded-2xl p-4 mt-5">
                <h3 className="font-bold text-green-800 mb-3">آخر نسخة تم إنشاؤها</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <Info label="اسم الملف" value={backupResult.file_name || '-'} />
                  <Info label="المسار" value={backupResult.path || backupResult.file_path || '-'} />
                  <Info label="التاريخ" value={formatDateTime(backupResult.created_at || new Date())} />
                  <Info label="الحالة" value="تم الإنشاء بنجاح" />
                </div>
              </div>
            )}
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
      <div className="mb-5">
        <h2 className="text-2xl font-bold text-gray-900">
          {title}
        </h2>
        {subtitle && (
          <p className="text-gray-500 text-sm mt-1">
            {subtitle}
          </p>
        )}
      </div>

      {children}
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="block mb-2 font-bold text-gray-700">
      {children}
    </label>
  );
}

function Input({ type = 'text', name, value, onChange, placeholder }) {
  return (
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      className="w-full border-2 border-gray-200 rounded-2xl p-3 bg-gray-50 focus:outline-none focus:border-orange-500"
      placeholder={placeholder}
    />
  );
}

function Textarea({ name, value, onChange, rows, placeholder }) {
  return (
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      rows={rows}
      className="w-full border-2 border-gray-200 rounded-2xl p-3 bg-gray-50 focus:outline-none focus:border-orange-500 resize-none"
      placeholder={placeholder}
    />
  );
}

function RuleCard({ title, text }) {
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4">
      <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 leading-7">{text}</p>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-white rounded-2xl p-3 border border-green-100">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="font-bold text-gray-900 break-words">{value}</p>
    </div>
  );
}

export default Settings;


function HealthCard({ label, value, hint }) {
  return (
    <div className="bg-white border border-gray-100 rounded-3xl shadow-sm p-5">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-2 break-words">{value}</p>
      <p className="text-xs text-gray-400 mt-2 break-words">{hint || '-'}</p>
    </div>
  );
}
